/**
 * /inventory/:id — count session detail.
 *
 * The counting loop itself (scanner, entry form, add-or-replace, receiving
 * checklist, counted list, submit/discard) is `CountSession` from
 * `@ampersandbrands/ui`, shared with Cockpit. This file is the fetch, the
 * store scoping, and the four IO functions the shared component calls.
 *
 * The scanner override keeps Sweet Spot's own `BarcodeScanner`, which adds a
 * `zxing-wasm` decoder for browsers without the native `BarcodeDetector` API.
 * That decoder is loaded via a Vite-only `?url` asset import the shared
 * package's plain esbuild build cannot resolve, so it stays here rather than
 * moving into the shared `BarcodeScanner` (see that component's own comment).
 */

import { useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CountSession as SharedCountSession } from '@ampersandbrands/ui';
import {
  useInventoryCountDetail,
  useItemLookup,
  useUpsertCountLine,
  useDeleteCountLine,
  useDeleteCountSession,
  useSubmitCountSession,
} from '@/lib/useInventory';
import { BarcodeScanner, type BarcodeScanResult } from '@/components/BarcodeScanner';
import { reportScannerEvent } from '@/lib/reportScannerEvent';
import { isNotAvailable } from '@/lib/useStores';
import { useHqStore } from '@/lib/useHqStore';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

export function CountSession() {
  const { id = '' } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { storeNo: hqStoreNo } = useHqStore();
  const storeNo = params.get('store_no') ? Number(params.get('store_no')) : (hqStoreNo ?? undefined);
  const storeQs = storeNo !== undefined ? `?store_no=${storeNo}` : '';

  const { data, isLoading, error } = useInventoryCountDetail(id, storeNo);
  const lookupM = useItemLookup();
  const upsertM = useUpsertCountLine(id);
  const deleteLineM = useDeleteCountLine(id);
  const deleteSessionM = useDeleteCountSession();
  const submitM = useSubmitCountSession(Number(id));
  const pendingScanRef = useRef<{ code: string; result: BarcodeScanResult } | null>(null);

  const session = data?.session ?? null;
  const lines = data?.lines ?? [];
  const poLines = data?.po_lines ?? [];

  // Layout's route-level default covers this page until the session kind is
  // known; once loaded, override with the specific flow name.
  useDocumentTitle([
    'Inventory',
    'Inventory Counts',
    ...(session ? [session.kind === 'receiving' ? 'Receive an order' : 'Start a Shelf Count'] : []),
  ]);

  function goBack() {
    if (session && session.status !== 'submitted' && lines.length === 0) {
      deleteSessionM.mutate({ sessionId: session.id, store_no: storeNo }, { onSettled: () => navigate(`/inventory${storeQs}`) });
      return;
    }
    navigate(`/inventory${storeQs}`);
  }

  const notAvailable = isNotAvailable(error);

  return (
    <div className="mx-auto w-full max-w-xl">
      <SharedCountSession
        session={session}
        lines={lines}
        poLines={poLines}
        loading={isLoading}
        error={
          notAvailable ? (
            <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
              This count session is not available.
            </p>
          ) : undefined
        }
        backLabel="Inventory counts"
        onBack={goBack}
        renderScanner={({ onCode }) => (
          <BarcodeScanner
            onBarcodeFound={(code, result) => {
              pendingScanRef.current = { code, result };
              onCode(code);
            }}
            storeNo={storeNo}
          />
        )}
        onLookup={async (code) => {
          const pending = pendingScanRef.current;
          pendingScanRef.current = null;
          const scan = pending?.code === code ? pending.result : null;
          const startedAt = Date.now();
          try {
            const d = await lookupM.mutateAsync({ barcode: code, ...(storeNo !== undefined ? { store_no: storeNo } : {}) });
            if (scan) {
              reportScannerEvent({
                type: d.found ? 'lookup_found' : 'lookup_not_found',
                source: scan.source,
                storeNo,
                barcodeLength: code.length,
                durationMs: Date.now() - startedAt,
              });
            }
            return d.found ? d.item : null;
          } catch (error) {
            if (scan) {
              reportScannerEvent({
                type: 'lookup_failed',
                source: scan.source,
                storeNo,
                barcodeLength: code.length,
                durationMs: Date.now() - startedAt,
              });
            }
            throw error;
          }
        }}
        onSaveCount={async ({ item_no, barcode, qty, mode }) => {
          await upsertM.mutateAsync({
            item_no,
            barcod: barcode ?? undefined,
            qty,
            mode,
            ...(storeNo !== undefined ? { store_no: storeNo } : {}),
          });
        }}
        saveError={
          upsertM.isError ? (
            <p className="mt-2 text-sm text-candy-pink dark:text-candy-pink-dark">
              Failed to add item. Please try again.
            </p>
          ) : undefined
        }
        onDeleteLine={(lineId) => deleteLineM.mutate({ lineId, store_no: storeNo })}
        deletingLine={deleteLineM.isPending}
        deleteLineError={
          deleteLineM.isError ? (
            <p className="mt-2 text-sm text-candy-pink dark:text-candy-pink-dark">
              Could not delete that count. Please try again.
            </p>
          ) : undefined
        }
        onDiscard={() =>
          deleteSessionM.mutate({ sessionId: Number(id), store_no: storeNo }, { onSettled: () => navigate(`/inventory${storeQs}`) })
        }
        discarding={deleteSessionM.isPending}
        onSubmit={() =>
          submitM.mutate({ ...(storeNo !== undefined ? { store_no: storeNo } : {}) }, { onSuccess: () => navigate(`/inventory${storeQs}`) })
        }
        submitting={submitM.isPending}
        submitError={
          submitM.isError ? (
            <p className="mt-2 text-sm text-candy-pink dark:text-candy-pink-dark">
              Could not submit the count. Please try again.
            </p>
          ) : undefined
        }
      />
    </div>
  );
}
